import { useMemo } from "react";
import { Field, inputCls, labelCls } from "@/components/Kit";
import { PhoneInput, GovIdInput } from "@/components/PhoneInput";
import { COUNTRIES, INDIAN_STATES, ageFromDob, validateEmail, validateGovId,
  validatePhone, validatePincode, splitE164 } from "@/lib/validate";

export const PATIENT_EMPTY = {
  name: "", gender: "male", dob: "", age: "", blood_group: "",
  phone: "", email: "",
  id_type: "", id_number: "", id_country: "IN", id_expiry: "",
  guardian_relation: "", guardian_name: "", guardian_phone: "",
  emergency_contact_name: "", emergency_contact_phone: "",
  area: "", address: "", city: "", district: "", state: "", country: "India", pincode: "",
  medical_history: "", allergies: "", medications: "", surgeries: "", notes: "",
};

const GUARDIAN_RELATIONS = ["", "father", "mother", "spouse", "son", "daughter", "brother",
  "sister", "guardian", "other"];

/** Mandatory fields for every patient registration entry point in the HMS. */
export function validatePatientForm(form) {
  const e = {};
  if (!String(form.name || "").trim()) e.name = "Full name is required.";
  else if (form.name.trim().length < 2) e.name = "Full name must be at least 2 characters.";
  if (!form.gender) e.gender = "Gender is required.";
  const { country, national } = splitE164(form.phone);
  const phoneErr = validatePhone(country, national, { required: true, label: "Mobile number" });
  if (phoneErr) e.phone = phoneErr;
  const emailErr = validateEmail(form.email);
  if (emailErr) e.email = emailErr;
  const idErr = validateGovId(form.id_type, form.id_number, form.id_country);
  if (idErr) e.id_number = idErr;
  if (!String(form.city || "").trim()) e.city = "City is required.";
  const pinErr = validatePincode(form.pincode);
  if (pinErr) e.pincode = pinErr;
  if (form.guardian_name && !form.guardian_relation) {
    e.guardian_relation = "Select the guardian's relationship.";
  }
  const g = splitE164(form.guardian_phone);
  if (g.national) {
    const gErr = validatePhone(g.country, g.national, { required: false, label: "Guardian mobile" });
    if (gErr) e.guardian_phone = gErr;
  }
  const em = splitE164(form.emergency_contact_phone);
  if (em.national) {
    const emErr = validatePhone(em.country, em.national, { required: false, label: "Emergency contact number" });
    if (emErr) e.emergency_contact_phone = emErr;
  }
  return e;
}

const Err = ({ msg, testid }) => msg
  ? <p className="mt-1.5 text-xs font-medium text-red-600" data-testid={testid}>{msg}</p> : null;

/** The one and only patient registration form — reused by Patients, OPD, IPD and Appointments. */
export function PatientForm({ form, setForm, errors = {}, prefix = "patient" }) {
  const set = (patch) => setForm({ ...form, ...patch });
  const autoAge = useMemo(() => ageFromDob(form.dob), [form.dob]);

  return (
    <div className="space-y-8" data-testid={`${prefix}-form`}>
      <section>
        <p className="overline text-[#EA580C] mb-4">Patient identity</p>
        <div className="grid gap-5 sm:grid-cols-3">
          <Field label="Full name *">
            <input className={inputCls} data-testid={`${prefix}-name-input`} value={form.name}
              onChange={(e) => set({ name: e.target.value })} />
            <Err msg={errors.name} testid={`${prefix}-name-error`} />
          </Field>
          <Field label="Gender *">
            <select className={inputCls} value={form.gender} data-testid={`${prefix}-gender-select`}
              onChange={(e) => set({ gender: e.target.value })}>
              <option value="male">Male</option><option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="Blood group">
            <select className={inputCls} value={form.blood_group} data-testid={`${prefix}-blood-select`}
              onChange={(e) => set({ blood_group: e.target.value })}>
              <option value="">Not known</option>
              {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </Field>
          <Field label="Date of birth">
            <input type="date" className={inputCls} value={form.dob} data-testid={`${prefix}-dob-input`}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => set({ dob: e.target.value, age: ageFromDob(e.target.value) })} />
            <Err msg={errors.dob} testid={`${prefix}-dob-error`} />
          </Field>
          <Field label="Age (years)">
            <input type="number" min="0" max="130" className={inputCls} data-testid={`${prefix}-age-input`}
              value={form.dob ? autoAge : form.age}
              readOnly={Boolean(form.dob)}
              onChange={(e) => set({ age: e.target.value })} />
            {form.dob && <p className="mt-1.5 text-xs text-slate-500">Calculated from date of birth.</p>}
          </Field>
        </div>
      </section>

      <section>
        <p className="overline text-[#EA580C] mb-4">Contact & government ID</p>
        <div className="grid gap-5 sm:grid-cols-3">
          <PhoneInput label="Mobile" required value={form.phone} testid={`${prefix}-phone`}
            error={errors.phone} onChange={(v) => set({ phone: v })} />
          <Field label="Email">
            <input className={inputCls} value={form.email} data-testid={`${prefix}-email-input`}
              onChange={(e) => set({ email: e.target.value })} />
            <Err msg={errors.email} testid={`${prefix}-email-error`} />
          </Field>
          <div />
          <GovIdInput type={form.id_type} number={form.id_number} country={form.id_country}
            testid={`${prefix}-govid`} error={errors.id_number}
            onChange={({ type, number, country }) => set({ id_type: type, id_number: number, id_country: country })} />
          {(form.id_type === "passport" || form.id_type === "driving_licence") && (
            <Field label="ID expiry date">
              <input type="date" className={inputCls} value={form.id_expiry}
                data-testid={`${prefix}-id-expiry-input`}
                onChange={(e) => set({ id_expiry: e.target.value })} />
            </Field>
          )}
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Format and checksum validation only — this does not verify the ID with the issuing authority.
        </p>
      </section>

      <section>
        <p className="overline text-[#EA580C] mb-4">Guardian & emergency contact</p>
        <div className="grid gap-5 sm:grid-cols-3">
          <Field label="Guardian relationship">
            <select className={inputCls} value={form.guardian_relation} data-testid={`${prefix}-guardian-relation`}
              onChange={(e) => set({ guardian_relation: e.target.value })}>
              {GUARDIAN_RELATIONS.map((r) => (
                <option key={r} value={r}>{r ? r[0].toUpperCase() + r.slice(1) : "Not applicable"}</option>
              ))}
            </select>
            <Err msg={errors.guardian_relation} testid={`${prefix}-guardian-relation-error`} />
          </Field>
          <Field label="Guardian name">
            <input className={inputCls} value={form.guardian_name} data-testid={`${prefix}-guardian-name`}
              onChange={(e) => set({ guardian_name: e.target.value })} />
          </Field>
          <PhoneInput label="Guardian mobile" value={form.guardian_phone} testid={`${prefix}-guardian-phone`}
            error={errors.guardian_phone} onChange={(v) => set({ guardian_phone: v })} />
          <Field label="Emergency contact name">
            <input className={inputCls} value={form.emergency_contact_name} data-testid={`${prefix}-emergency-name`}
              onChange={(e) => set({ emergency_contact_name: e.target.value })} />
          </Field>
          <PhoneInput label="Emergency contact number" value={form.emergency_contact_phone}
            testid={`${prefix}-emergency-phone`} error={errors.emergency_contact_phone}
            onChange={(v) => set({ emergency_contact_phone: v })} />
        </div>
      </section>

      <section>
        <p className="overline text-[#EA580C] mb-4">Address</p>
        <div className="grid gap-5 sm:grid-cols-3">
          <Field label="Area / locality">
            <input className={inputCls} value={form.area} data-testid={`${prefix}-area-input`}
              onChange={(e) => set({ area: e.target.value })} />
          </Field>
          <Field label="City *">
            <input className={inputCls} value={form.city} data-testid={`${prefix}-city-input`}
              onChange={(e) => set({ city: e.target.value })} />
            <Err msg={errors.city} testid={`${prefix}-city-error`} />
          </Field>
          <Field label="District">
            <input className={inputCls} value={form.district} data-testid={`${prefix}-district-input`}
              onChange={(e) => set({ district: e.target.value })} />
          </Field>
          <Field label="State">
            {form.country === "India" ? (
              <select className={inputCls} value={form.state} data-testid={`${prefix}-state-select`}
                onChange={(e) => set({ state: e.target.value })}>
                <option value="">Select state</option>
                {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            ) : (
              <input className={inputCls} value={form.state} data-testid={`${prefix}-state-input`}
                onChange={(e) => set({ state: e.target.value })} />
            )}
          </Field>
          <Field label="Country">
            <select className={inputCls} value={form.country} data-testid={`${prefix}-country-select`}
              onChange={(e) => set({ country: e.target.value, state: "" })}>
              {["India", ...COUNTRIES.filter((c) => c.code !== "IN").map((c) => c.name)]
                .map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="PIN / postal code">
            <input className={inputCls} value={form.pincode} data-testid={`${prefix}-pincode-input`}
              onChange={(e) => set({ pincode: e.target.value })} />
            <Err msg={errors.pincode} testid={`${prefix}-pincode-error`} />
          </Field>
          <Field label="Street address" className="sm:col-span-3">
            <input className={inputCls} value={form.address} data-testid={`${prefix}-address-input`}
              onChange={(e) => set({ address: e.target.value })} />
          </Field>
        </div>
      </section>

      <section>
        <p className="overline text-[#EA580C] mb-4">Clinical background</p>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Medical history">
            <textarea rows={2} className={inputCls} value={form.medical_history}
              data-testid={`${prefix}-history-input`}
              onChange={(e) => set({ medical_history: e.target.value })} />
          </Field>
          <Field label="Known allergies">
            <textarea rows={2} className={inputCls} value={form.allergies}
              data-testid={`${prefix}-allergies-input`}
              onChange={(e) => set({ allergies: e.target.value })} />
          </Field>
          <Field label="Current medications">
            <textarea rows={2} className={inputCls} value={form.medications}
              data-testid={`${prefix}-medications-input`}
              onChange={(e) => set({ medications: e.target.value })} />
          </Field>
          <Field label="Previous surgeries">
            <textarea rows={2} className={inputCls} value={form.surgeries}
              data-testid={`${prefix}-surgeries-input`}
              onChange={(e) => set({ surgeries: e.target.value })} />
          </Field>
          <Field label="Additional notes" className="sm:col-span-2">
            <textarea rows={2} className={inputCls} value={form.notes} data-testid={`${prefix}-notes-input`}
              onChange={(e) => set({ notes: e.target.value })} />
          </Field>
        </div>
      </section>
    </div>
  );
}

export const patientPayload = (form) => ({
  ...form,
  age: form.dob ? Number(ageFromDob(form.dob) || 0) : (form.age ? Number(form.age) : null),
});

export { labelCls };
